import { useEffect, useRef } from "react"
import { formatName, isLocal, isUat } from "@/utils/helpers/formatters"
import { useSelector } from "react-redux"
import { selectMemberDetails } from "@/store/apis/linkApi"

/**
 * React component that initializes the Genesys web chat widget by:
 * - Observing the DOM for the chat input to become available
 * - Prefilling the user's name and email address when available
 * - Optionally disabling the form fields to prevent editing
 *
 * This component is intended to be used alongside the GESB Genesys chat integration (`chat-widget-v2`)
 * which loads the `window.Genesys` global object. It disconnects the MutationObserver once initialization is complete.
 *
 * @component
 * @returns {null} This component does not render anything to the DOM.
 */
function GenesysWebChatInitializer() {
  const { givenNames, lastName, emailAddress } =
    useSelector(selectMemberDetails)

  // Helper to set and optionally disable an input
  const setField = (name: string, value: string, setDisabled = true) => {
    const el = document.getElementById(`gs-${name}`) as HTMLInputElement | null
    if (el) {
      el.value = value
      if (setDisabled) el.setAttribute("disabled", "disabled")
    }
  }

  const observerRef = useRef<MutationObserver>()

  useEffect(() => {
    // Only start watching once we have user details
    if (!givenNames || !lastName) {
      return
    }

    // Callback for mutations & Genesys readiness
    const tryInitialize = () => {
      const chatInput = document.getElementById("gs-input")
      if (chatInput && (window as any).Genesys) {
        // Prefill fields
        const fullName = `${formatName(givenNames)} ${formatName(lastName)}`
        setField("fname", fullName)
        setField("email", emailAddress || "")
        setField("subject", "", false)

        if (isLocal() || isUat()) {
          console.log("Prefilling chat window: ", `${fullName}`)
        }
        // Cleanup observer
        observerRef.current?.disconnect()
      }
    }

    // Create observer if not already
    if (!observerRef.current) {
      observerRef.current = new MutationObserver(() => {
        tryInitialize()
      })
      observerRef.current.observe(document.body, {
        childList: true,
        subtree: true,
      })
    }

    // Also run once immediately in case elements are already present
    tryInitialize()

    // Cleanup on unmount
    return () => {
      observerRef.current?.disconnect()
    }
  }, [givenNames, lastName, emailAddress])

  return null
}

export default GenesysWebChatInitializer
